/**
 * Per-level progress, kept on the device.
 *
 * The server already receives every result, so this is only about showing the
 * player where they are. It is deliberately best-effort: losing it costs a few
 * badges, not a record.
 */

/**
 * A score at or above this counts the case as closed.
 *
 * Defined once. The verdict stamp, the case-file stamp and the per-tool count
 * on the picker all mean the same thing by "closed", and three copies of 70
 * would eventually stop agreeing.
 */
export const SOLVED_AT = 70;

export interface LevelProgress {
  bestScore: number;
  plays: number;
  lastPlayedAt: string;
}

export type Progress = Record<string, LevelProgress>;

const STORAGE_KEY = 'devtool-detective.progress';

export function loadProgress(): Progress {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Progress) : {};
  } catch {
    return {};
  }
}

/**
 * Wipe local progress.
 *
 * Only clears this device's record of best scores. Results already submitted to
 * the server are untouched, and the UI must say so — a "reset" that silently
 * left a server-side trail would be misleading.
 */
export function clearProgress(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* best effort */
  }
}

export function recordLevelResult(
  progress: Progress,
  levelId: string,
  score: number,
): Progress {
  const previous = progress[levelId];
  const next: Progress = {
    ...progress,
    [levelId]: {
      bestScore: Math.max(previous?.bestScore ?? 0, score),
      plays: (previous?.plays ?? 0) + 1,
      lastPlayedAt: new Date().toISOString(),
    },
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* best effort */
  }
  return next;
}
